import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { editSection, getAllPageSections, getSection } from "store/actions";
import Loader from "components/shared/Loader";
import { Input } from "components/shared/FormComponents";
import SaveChangesButton from "components/shared/SaveChangesButton";
import { toastErrorMessage } from "helpers/toaster/toastErrorMessage";
import "flatpickr/dist/flatpickr.css";
import ReactSelect from "react-select";
import ImagesUpload from "components/shared/ImagesUpload";
import { v4 } from "uuid";
import SlidersArray from "./SliderArray";
import { numInputValidations } from "helpers/functions";
import { Editor } from "components/shared/Editor";

const EditProjects = () => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { loading, error, section, sections } = useSelector(
    (state) => state.section
  );
  const navigate = useNavigate();
  const [slider, setSlider] = useState([]);
  const [features, setFeatures] = useState([]);
  const [description, setDescription] = useState({});
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm();
  // effects
  useEffect(() => {
    dispatch(getSection({ page: "projects", slug: id }));
    dispatch(getAllPageSections("categories-1"));
  }, [dispatch, id]);

  useEffect(() => {
    reset(section);

    let images = [];
    section?.images?.forEach((imagePath) => {
      const image = {
        id: v4(),
        preview: imagePath,
        path: imagePath,
      };
      images.push(image);
    });

    setSlider(images);
    setFeatures(section.features);
  }, [reset, section]);

  // error
  useEffect(() => {
    if (error) {
      toastErrorMessage(formatMessage({ id: "sendingErrorMessage" }), locale);
    }
  }, [error, formatMessage, locale]);

  const onSubmit = (data) => {
    data.images = slider.map((image) => {
      return image.path;
    });
    data.features = features;
    data.description = description;
    dispatch(
      editSection({
        slug: id,
        page: "projects",
        data: { section: data },
        navigate: () => navigate("/projects"),
      })
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="acc-form">
        <div className="card">
          <div className="card-head">
            <h4>
              <FormattedMessage id={"editItem"} />
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col lg={12} xs={12}>
                  <div className="form-group ">
                    <h5>
                      <FormattedMessage id={"images"} />
                    </h5>
                    <ImagesUpload
                      imagesURLs={slider}
                      setImageURLs={setSlider}
                    />
                  </div>
                </Col>
                <Row />
                {/* categories dropdown */}
                <Col xs={12}>
                  <div className="form-group ">
                    <h5>
                      <FormattedMessage id="category" />
                    </h5>

                    <Controller
                      control={control}
                      name="category"
                      rules={{ required: true }}
                      render={({ field: { onChange, value, name, ref } }) => (
                        <ReactSelect
                          inputRef={ref}
                          name={name}
                          placeholder={section?.category?.title?.[locale]}
                          options={sections.map((c) => ({
                            value: c?.slug,
                            label: c?.sectionTitle?.[locale],
                            response: c,
                          }))}
                          onChange={(selectedOption) => {
                            onChange(selectedOption.response);
                          }}
                          isRtl={true}
                          isSearchable={true}
                          className="basic-single"
                          classNamePrefix="select"
                        />
                      )}
                    />
                  </div>
                </Col>
                {/* availability dropdown*/}
                <Col xs={12}>
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="isAvailable"
                        className="form-check-input"
                        {...register("isAvailable")}
                      />
                      <label htmlFor="isAvailable" className="form-check-label">
                        <h5>
                          <FormattedMessage id="availability" />
                        </h5>
                      </label>
                    </div>
                  </div>
                </Col>
                <Col lg={6} xs={12}>
                  <Input
                    langId="title"
                    name="projectTitle.ar"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col lg={6} xs={12}>
                  <Input
                    langId="title"
                    name="projectTitle.en"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col lg={6} xs={12}>
                  <div>
                    <Controller
                      control={control}
                      name="description.ar"
                      render={({ field: { value } }) => (
                        <Editor
                          initialValue={section?.description?.ar}
                          onChange={(e, editor) => {
                            const data = editor.getContent();
                            setDescription((prevState) => {
                              const nextState = { ...prevState, ar: data };
                              return nextState;
                            });
                          }}
                          tinymceScriptSrc="https://cdn.tiny.cloud/1/qagffr3pkuv17a8on1afax661irst1hbr4e6tbv888sz91jc/tinymce/5-stable/tinymce.min.js"
                        />
                      )}
                    />
                    <p className="error-hint">
                      {errors.description?.ar?.type === "required" && (
                        <FormattedMessage id="required" />
                      )}
                    </p>
                  </div>
                </Col>
                <Col lg={6} xs={12}>
                  <div>
                    <Controller
                      control={control}
                      name="description.en"
                      render={({ field: { value } }) => (
                        <Editor
                          initialValue={section?.description?.en}
                          onChange={(e, editor) => {
                            const data = editor.getContent();
                            setDescription((prevState) => {
                              const nextState = { ...prevState, en: data };
                              return nextState;
                            });
                          }}
                          tinymceScriptSrc="https://cdn.tiny.cloud/1/qagffr3pkuv17a8on1afax661irst1hbr4e6tbv888sz91jc/tinymce/5-stable/tinymce.min.js"
                        />
                      )}
                    />
                    <p className="error-hint">
                      {errors.description?.ar?.type === "required" && (
                        <FormattedMessage id="required" />
                      )}
                    </p>
                  </div>
                </Col>
                <Col lg={6} xs={12}>
                  <Input
                    langId="location"
                    name="location.ar"
                    register={register}
                    rules={{ required: true }}
                    errors={errors}
                  />
                </Col>
                <Col lg={6} xs={12}>
                  <Input
                    langId="location"
                    name="location.en"
                    register={register}
                    rules={{ required: true }}
                    errors={errors}
                  />
                </Col>
                <Col lg={6} xs={12}>
                  <Input
                    langId="price"
                    name="price"
                    register={register}
                    rules={{ required: true }}
                    errors={errors}
                    {...numInputValidations}
                  />
                </Col>
              </Row>
              <SlidersArray sliders={features} setSliders={setFeatures} />
              <SaveChangesButton />
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProjects;
